import { useCallback, useEffect, useState } from 'react'
import { formatHumanName } from '@medplum/core'
import type { Organization, Patient, Practitioner } from '@medplum/fhirtypes'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  IconAlertCircle,
  IconAt,
  IconCheck,
  IconDownload,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react'
import {
  type FollowupPdfProviderHeader,
  generateFollowupInstructionsPdf,
} from './generateFollowupInstructionsPdf'
import { useFollowupActions } from './useFollowupActions'
import { useIsManager } from '@/hooks/useIsManager'
import { logError } from '@/utils/errorUtils'
import { formatProviderName } from '@/utils/providerUtils'
import { useAutoPopulate } from '../OrderTests/shared/useAutoPopulate'
import {
  formatFhirDob,
  getPatientAddress,
  getPatientEmail,
  getPatientPhone,
  getPatientSex,
} from '../OrderTests/shared/formHelpers'
import { downloadPdfBlob } from '../OrderTests/pdf/pdfHelpers'
import type { ProviderDefaults } from '../OrderTests/shared/types'

const textareaStyles = {
  input: {
    fontSize: '15px',
    lineHeight: 1.65,
    padding: '18px 20px',
  },
}

type Props = {
  opened: boolean
  onClose: () => void
  onSaved?: () => void
  isAlreadySaved?: boolean
  documentReferenceId: string
  patientId: string
  patient: Patient
  patientName?: string
  providerName?: string
  sourceName?: string
  providerReference?: string
  sourceReference?: string
}

type SaveStatus = 'idle' | 'saved' | 'sent'

function buildFallbackProviderHeader(
  providerName?: string,
  sourceName?: string,
  providerDefaults?: ProviderDefaults,
): FollowupPdfProviderHeader {
  return {
    organizationName:
      (sourceName && sourceName !== '-' ? sourceName : '') ||
      providerDefaults?.organizationName ||
      '',
    providerName:
      (providerName && providerName !== '-' ? providerName : '') ||
      providerDefaults?.providerName ||
      '',
    npi: providerDefaults?.npi || '',
    phone: providerDefaults?.orgPhone || '',
    fax: providerDefaults?.orgFax || '',
    address: providerDefaults?.orgAddress || '',
    city: providerDefaults?.orgCity || '',
    state: providerDefaults?.orgState || '',
    zip: providerDefaults?.orgZip || '',
    email: providerDefaults?.providerEmail || '',
  }
}

function buildProviderHeader(
  fallback: FollowupPdfProviderHeader,
  practitioner?: Practitioner,
  organization?: Organization,
): FollowupPdfProviderHeader {
  const address = organization?.address?.[0] ?? practitioner?.address?.[0]

  return {
    ...fallback,
    organizationName: organization?.name || fallback.organizationName,
    providerName: practitioner?.name?.[0]
      ? formatProviderName(practitioner.name[0])
      : fallback.providerName,
    npi:
      practitioner?.identifier?.find((id) => id.system === 'http://hl7.org/fhir/sid/us-npi')
        ?.value || fallback.npi,
    phone: organization?.telecom?.find((item) => item.system === 'phone')?.value || fallback.phone,
    fax:
      organization?.telecom?.find((item) => item.system === 'fax')?.value ||
      practitioner?.telecom?.find((item) => item.system === 'fax')?.value ||
      fallback.fax,
    address: address?.line?.join(', ') || fallback.address,
    city: address?.city || fallback.city,
    state: address?.state || fallback.state,
    zip: address?.postalCode || fallback.zip,
    email:
      practitioner?.telecom?.find((item) => item.system === 'email')?.value ||
      organization?.telecom?.find((item) => item.system === 'email')?.value ||
      fallback.email,
  }
}

function buildPatientHeader(patient: Patient, patientName?: string) {
  const address = getPatientAddress(patient)

  return {
    name: patientName || (patient.name?.[0] ? formatHumanName(patient.name[0]) : ''),
    dateOfBirth: formatFhirDob(patient.birthDate),
    sex: getPatientSex(patient),
    phone: getPatientPhone(patient),
    email: getPatientEmail(patient),
    address: address.line,
    city: address.city,
    state: address.state,
    zip: address.zip,
  }
}

export function ReviewFollowupModal({
  opened,
  onClose,
  onSaved,
  isAlreadySaved,
  documentReferenceId,
  patientId,
  patient,
  patientName,
  providerName,
  sourceName,
  providerReference,
  sourceReference,
}: Props) {
  const medplum = useMedplum()
  const profile = useMedplumProfile() as Practitioner | undefined
  const { providerDefaults } = useAutoPopulate(patient)
  const { generate, saveAndNotify } = useFollowupActions()
  const { isManager, loading: isManagerLoading } = useIsManager()

  const [instructionsSubject, setInstructionsSubject] = useState('')
  const [instructionsBody, setInstructionsBody] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [patientEmail, setPatientEmail] = useState<string | undefined>()
  const [generated, setGenerated] = useState(false)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [isPreparingPdf, setIsPreparingPdf] = useState(false)
  const [isPreviewingPdf, setIsPreviewingPdf] = useState(false)
  const [providerHeader, setProviderHeader] = useState<FollowupPdfProviderHeader>(() =>
    buildFallbackProviderHeader(providerName, sourceName, providerDefaults),
  )

  const resolveProviderHeader = useCallback(async () => {
    const fallbackHeader = buildFallbackProviderHeader(providerName, sourceName, providerDefaults)
    let practitioner: Practitioner | undefined
    let organization: Organization | undefined

    try {
      if (providerReference?.startsWith('Practitioner/')) {
        practitioner = await medplum.readResource(
          'Practitioner',
          providerReference.slice('Practitioner/'.length),
        )
      } else if (profile?.resourceType === 'Practitioner') {
        practitioner = profile
      }
    } catch (err) {
      logError('Follow-up PDF practitioner lookup', err)
    }

    try {
      if (sourceReference?.startsWith('Organization/')) {
        organization = await medplum.readResource(
          'Organization',
          sourceReference.slice('Organization/'.length),
        )
      }
    } catch (err) {
      logError('Follow-up PDF source organization lookup', err)
    }

    if (!organization && practitioner?.id) {
      try {
        const role = await medplum.searchOne(
          'PractitionerRole',
          `practitioner=Practitioner/${practitioner.id}`,
        )
        if (role?.organization?.reference) {
          organization = (await medplum.readReference(role.organization)) as Organization
        }
      } catch (err) {
        logError('Follow-up PDF practitioner organization lookup', err)
      }
    }

    return buildProviderHeader(fallbackHeader, practitioner, organization)
  }, [
    medplum,
    profile,
    providerDefaults,
    providerName,
    providerReference,
    sourceName,
    sourceReference,
  ])

  const handleGenerate = () => {
    setSaveStatus('idle')
    generate.mutate(
      { documentReferenceId, patientId },
      {
        onSuccess: (data) => {
          setInstructionsSubject(data.instructionsSubject)
          setInstructionsBody(data.instructionsBody)
          setEmailSubject(data.emailSubject)
          setEmailBody(data.emailBody)
          setPatientEmail(data.patientEmail)
          setGenerated(true)
        },
        onError: () => {
          showNotification({
            title: 'Error',
            message: 'Failed to generate patient instructions',
            color: 'red',
          })
        },
      },
    )
  }

  useEffect(() => {
    if (!opened) {
      return
    }

    setGenerated(false)
    setInstructionsSubject('')
    setInstructionsBody('')
    setEmailSubject('')
    setEmailBody('')
    setPatientEmail(undefined)
    setSaveStatus('idle')
    setIsPreparingPdf(false)
    setIsPreviewingPdf(false)
    generate.reset()
    saveAndNotify.reset()
    handleGenerate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentReferenceId, opened, patientId])

  useEffect(() => {
    if (!opened) {
      return
    }

    let cancelled = false
    const fallbackHeader = buildFallbackProviderHeader(providerName, sourceName, providerDefaults)
    setProviderHeader(fallbackHeader)

    resolveProviderHeader()
      .then((header) => {
        if (!cancelled) {
          setProviderHeader(header)
        }
      })
      .catch((err) => {
        logError('Follow-up PDF provider header resolution', err)
      })

    return () => {
      cancelled = true
    }
  }, [opened, providerDefaults, providerName, resolveProviderHeader, sourceName])

  const buildInstructionsPdf = useCallback(
    () =>
      generateFollowupInstructionsPdf({
        instructionsSubject,
        instructionsBody,
        patient: buildPatientHeader(patient, patientName),
        providerName,
        sourceName,
        providerHeader,
      }),
    [
      instructionsBody,
      instructionsSubject,
      patient,
      patientName,
      providerHeader,
      providerName,
      sourceName,
    ],
  )

  const handlePreviewPdf = async () => {
    setIsPreviewingPdf(true)

    try {
      const instructionsPdf = await buildInstructionsPdf()
      const blob = new Blob([instructionsPdf.bytes], { type: 'application/pdf' })
      downloadPdfBlob(blob, instructionsPdf.fileName)
      showNotification({
        title: 'PDF Preview',
        message: 'The patient instructions PDF has been downloaded for preview.',
        color: 'blue',
      })
    } catch (error) {
      logError('Follow-up PDF preview', error)
      showNotification({
        title: 'Error',
        message: 'Failed to generate patient instructions PDF',
        color: 'red',
      })
    } finally {
      setIsPreviewingPdf(false)
    }
  }

  const handleSaveAndNotify = async () => {
    setIsPreparingPdf(true)

    let instructionsPdf: Awaited<ReturnType<typeof generateFollowupInstructionsPdf>>
    try {
      instructionsPdf = await buildInstructionsPdf()
    } catch {
      setIsPreparingPdf(false)
      showNotification({
        title: 'Error',
        message: 'Failed to generate patient instructions PDF',
        color: 'red',
      })
      return
    }

    saveAndNotify.mutate(
      {
        patientId,
        sourceDocumentReferenceId: documentReferenceId,
        instructionsSubject,
        instructionsBody,
        emailSubject,
        emailBody,
        sendEmail: canSendEmail,
        instructionsPdfBase64: instructionsPdf.base64,
        instructionsPdfFileName: instructionsPdf.fileName,
      },
      {
        onSuccess: (data) => {
          setIsPreparingPdf(false)
          if (data.emailSkipCode === 'duplicate') {
            setSaveStatus('saved')
            onSaved?.()
            showNotification({
              title: 'Already saved',
              message:
                data.emailSkipReason ||
                'Patient instructions already exist for this progress note.',
              color: 'yellow',
            })
            return
          }

          setSaveStatus(data.emailSent ? 'sent' : 'saved')
          onSaved?.()
          if (data.emailSent) {
            showNotification({
              title: 'Success',
              message: 'Patient instructions saved to documents and notification email sent',
              color: 'green',
            })
          } else {
            showNotification({
              title: 'Saved',
              message: `Patient instructions saved to documents. ${data.emailSkipReason || 'Email notification was not sent.'}`,
              color: 'yellow',
            })
          }
        },
        onError: () => {
          setIsPreparingPdf(false)
          showNotification({
            title: 'Error',
            message: 'Failed to save followup document',
            color: 'red',
          })
        },
      },
    )
  }

  const handleClose = () => {
    setGenerated(false)
    setInstructionsSubject('')
    setInstructionsBody('')
    setEmailSubject('')
    setEmailBody('')
    setPatientEmail(undefined)
    setSaveStatus('idle')
    setIsPreparingPdf(false)
    setIsPreviewingPdf(false)
    generate.reset()
    saveAndNotify.reset()
    onClose()
  }

  const isSaved = saveStatus !== 'idle' || !!isAlreadySaved
  const canSendEmail = Boolean(patientEmail)
  // Managers (care coordinators) lack permission to save the follow-up document
  // — the save-and-notify request is rejected server-side — so block the action
  // while still letting them review and generate the draft. Also block until the
  // role lookup settles so a manager can't catch the button enabled mid-load.
  const isSaveDisabled =
    isManagerLoading ||
    isManager ||
    isSaved ||
    saveAndNotify.isPending ||
    isPreparingPdf ||
    isPreviewingPdf
  const saveButtonLabel =
    saveStatus === 'sent'
      ? 'Saved & Sent'
      : isSaved
        ? 'Saved'
        : canSendEmail
          ? 'Save Instructions & Send Email'
          : 'Save Instructions'
  const title = (
    <Stack gap={2}>
      <Text fw={700} size="xl" c="#121827">
        Review Patient Instructions
      </Text>
      <Text size="sm" c="#66738a" fw={400}>
        Review what will be saved to the patient chart and what email will be sent to the patient.
      </Text>
    </Stack>
  )

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      size="min(1180px, calc(100vw - 48px))"
      centered
      radius="lg"
      padding={0}
      styles={{
        header: {
          padding: '28px 32px',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        },
        body: {
          background: '#f8fafc',
        },
        close: {
          color: '#66738a',
        },
      }}
    >
      {generate.isPending && !generated ? (
        <Stack align="center" py={64} gap="md">
          <Loader size="md" />
          <Text c="dimmed" size="sm">
            Generating patient-friendly summary...
          </Text>
        </Stack>
      ) : generated ? (
        <Stack gap={0}>
          {!patientEmail && (
            <Box px={24} pt={20}>
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                Patient does not have an email on file. The instructions can be saved to the chart,
                but no email notification will be sent.
              </Alert>
            </Box>
          )}

          <Stack p={24} gap="xl">
            <Paper
              radius="md"
              p={22}
              withBorder
              style={{
                borderColor: '#cfe1ff',
                background: '#eef5ff',
              }}
            >
              <Stack gap="lg">
                <Group align="flex-start" gap="md" wrap="nowrap">
                  <Box
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: '#dbeafe', color: '#2854d8' }}
                  >
                    <IconCheck size={22} stroke={2.5} />
                  </Box>
                  <Stack gap={6}>
                    <Group gap="sm" wrap="wrap">
                      <Text fw={700} size="lg" c="#172033">
                        Patient Chart Instructions
                      </Text>
                      <Badge
                        variant="light"
                        color="blue"
                        radius="xl"
                        tt="none"
                        styles={{ label: { fontSize: 12 } }}
                      >
                        AI draft · provider review required
                      </Badge>
                    </Group>
                    <Text size="sm" c="#526178" lh={1.55}>
                      This text was generated from the progress note. Please review and edit before
                      saving. Once approved, this is the visit summary/instructions saved to the
                      patient chart and shown in the patient portal.
                    </Text>
                  </Stack>
                </Group>

                <Stack gap={8}>
                  <Text fw={700} size="sm" c="#334155">
                    Text to Save to Patient Chart
                  </Text>
                  <Textarea
                    aria-label="Patient instructions"
                    value={instructionsBody}
                    onChange={(e) => {
                      setInstructionsBody(e.currentTarget.value)
                      setSaveStatus('idle')
                    }}
                    minRows={10}
                    maxRows={14}
                    autosize
                    radius="md"
                    styles={textareaStyles}
                  />
                </Stack>

                <Alert color="yellow" variant="light" radius="md">
                  <Text size="sm" c="#8a4217">
                    <Text span fw={700} inherit>
                      Clinical review required:
                    </Text>{' '}
                    Confirm medication instructions, procedure details, and follow-up timing before
                    saving.
                  </Text>
                </Alert>
              </Stack>
            </Paper>

            <Paper radius="md" p={22} withBorder bg="white">
              <Stack gap="lg">
                <Group align="flex-start" gap="md" wrap="nowrap">
                  <Box
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: '#f1f5f9', color: '#334155' }}
                  >
                    <IconAt size={21} stroke={2.2} />
                  </Box>
                  <Stack gap={6}>
                    <Group gap="sm" wrap="wrap">
                      <Text fw={700} size="lg" c="#172033">
                        Patient Email Notification
                      </Text>
                      {patientEmail ? (
                        <Badge variant="light" color="gray" radius="xl" tt="none">
                          {patientEmail}
                        </Badge>
                      ) : null}
                    </Group>
                    <Text size="sm" c="#526178" lh={1.55}>
                      This email will be sent to the patient. It does not include the full visit
                      summary. It only tells the patient to log in to the patient portal to review
                      the approved summary.
                    </Text>
                  </Stack>
                </Group>

                <Stack gap={8}>
                  <TextInput
                    label="Email Subject"
                    size="md"
                    value={emailSubject}
                    radius="md"
                    onChange={(e) => {
                      setEmailSubject(e.currentTarget.value)
                      setSaveStatus('idle')
                    }}
                  />
                  <Textarea
                    label="Email Body"
                    aria-label="Patient notification email"
                    value={emailBody}
                    onChange={(e) => {
                      setEmailBody(e.currentTarget.value)
                      setSaveStatus('idle')
                    }}
                    minRows={6}
                    maxRows={9}
                    autosize
                    radius="md"
                    styles={textareaStyles}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Stack>

          <Divider />
          <Group justify="space-between" px={24} py={18} bg="white">
            <Group gap="sm">
              <Button
                size="md"
                variant="default"
                leftSection={<IconRefresh size={16} />}
                onClick={handleGenerate}
                loading={generate.isPending}
                disabled={isSaved}
              >
                Regenerate
              </Button>
              <Button
                size="md"
                variant="default"
                leftSection={<IconDownload size={16} />}
                onClick={() => void handlePreviewPdf()}
                loading={isPreviewingPdf}
                disabled={saveAndNotify.isPending || isPreparingPdf}
              >
                Preview PDF
              </Button>
            </Group>

            <Tooltip
              label="Practitioner permissions required to save patient instructions"
              disabled={!isManager}
            >
              <Button
                size="md"
                variant="filled"
                color={saveStatus === 'sent' ? 'green' : 'blue'}
                onClick={isSaveDisabled ? undefined : handleSaveAndNotify}
                loading={saveAndNotify.isPending || isPreparingPdf}
                leftSection={
                  isSaved || !canSendEmail ? <IconCheck size={16} /> : <IconSend size={16} />
                }
                data-disabled={isSaveDisabled || undefined}
              >
                {saveButtonLabel}
              </Button>
            </Tooltip>
          </Group>
        </Stack>
      ) : (
        <Stack align="center" py={64}>
          <Text c="dimmed" size="sm">
            Click below to generate a follow-up email from this progress note.
          </Text>
          <Button onClick={handleGenerate}>Generate Follow-up</Button>
        </Stack>
      )}
    </Modal>
  )
}
